import React, {useEffect, useState} from "react";
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, MenuItem,
  Stack, Switch, TextField, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {MailConnectorRepository} from "../../../repositories/MailConnectorRepository";
import useConfirm from "../../../hooks/useConfirm";

const SECURITY_OPTIONS = [
  {value: "STARTTLS", label: "STARTTLS", port: 587},
  {value: "SSL", label: "SSL / TLS", port: 465},
  {value: "NONE", label: "None", port: 25},
];

const portOf = (security) => SECURITY_OPTIONS.find(option => option.value === security)?.port;

const EMPTY_FORM = {
  name: "", enabled: true, host: "", port: 587, security: "STARTTLS", username: "", password: "", removePassword: false,
  fromAddress: "", fromName: "", replyTo: "",
};

const sectionTitleSx = {fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: "#8A8AA0", mb: 1};
const buttonSx = (variant) => ({
  height: 38, px: 2, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600,
  ...(variant === "dark" && {color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}, "&.Mui-disabled": {color: "#fff", bgcolor: "#C9CAD8"}}),
  ...(variant === "light" && {color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"}}),
  ...(variant === "danger" && {color: "#C73A3A", "&:hover": {bgcolor: "#FDECEC"}}),
});

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.response?.data?.error || fallback;

// Connects the project's mailbox (SMTP), or edits / removes the connected one.
// connectorId: the connector to edit, null to create one. onSaved(): after a create, edit or delete.
export const MailConnectorDialog = ({open, projectId, connectorId, onClose, onSaved}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [passwordSet, setPasswordSet] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = useConfirm();
  const isEdit = Boolean(connectorId);
  const busy = saving || deleting;

  useEffect(() => {
    if (!open) return;
    setPasswordSet(false);
    if (!connectorId) {
      setForm(EMPTY_FORM);
      return;
    }
    let cancelled = false;
    setLoading(true);
    MailConnectorRepository.get(connectorId)
      .then(({data}) => {
        if (cancelled) return;
        setForm({
          ...EMPTY_FORM,
          name: data.name || "",
          enabled: data.enabled !== false,
          host: data.host || "",
          port: data.port || portOf(data.security) || "",
          security: data.security || "STARTTLS",
          username: data.username || "",
          fromAddress: data.fromAddress || "",
          fromName: data.fromName || "",
          replyTo: data.replyTo || "",
        });
        setPasswordSet(Boolean(data.passwordSet));
      })
      .catch(error => {
        if (cancelled) return;
        toast.error(errorMessage(error, "Failed to load the mail connector"));
        onClose();
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, connectorId, onClose]);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(previous => ({...previous, [key]: value}));
  };

  // the port follows the security while it is still the default of the previous one
  const changeSecurity = (e) => {
    const security = e.target.value;
    setForm(previous => ({
      ...previous,
      security,
      port: !previous.port || Number(previous.port) === portOf(previous.security) ? portOf(security) : previous.port,
    }));
  };

  const valid = form.name.trim() && form.host.trim() && form.fromAddress.trim();

  // every field is sent, so a cleared username / From name / Reply-To is cleared on edit too; an empty password
  // keeps the stored one unless "Remove the saved password" is checked
  const save = () => {
    const body = {
      name: form.name.trim(),
      enabled: form.enabled,
      host: form.host.trim(),
      port: form.port ? Number(form.port) : undefined,
      security: form.security,
      username: form.username.trim(),
      password: form.password || undefined,
      removePassword: isEdit && !form.password && form.removePassword,
      fromAddress: form.fromAddress.trim(),
      fromName: form.fromName.trim(),
      replyTo: form.replyTo.trim(),
    };
    setSaving(true);
    (isEdit ? MailConnectorRepository.edit(connectorId, body) : MailConnectorRepository.create({...body, projectId}))
      .then(() => {
        toast.success(isEdit ? "Mail connector updated" : "Mailbox connected");
        onSaved();
        onClose();
      })
      .catch(error => toast.error(errorMessage(error, isEdit ? "Failed to update the mail connector" : "Failed to connect the mailbox")))
      .finally(() => setSaving(false));
  };

  const remove = async () => {
    const confirmed = await confirmDelete({
      title: "Disconnect this mailbox?",
      message: "Mails will be sent from the default OpenBrain account until you connect a mailbox again.",
      confirmText: "Disconnect",
    });
    if (!confirmed) return;
    setDeleting(true);
    MailConnectorRepository.delete(connectorId)
      .then(() => {
        toast.success("Mailbox disconnected");
        onSaved();
        onClose();
      })
      .catch(error => toast.error(errorMessage(error, "Failed to disconnect the mailbox")))
      .finally(() => setDeleting(false));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth={"sm"}
            PaperProps={{sx: {borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)"}}}>
      <Stack direction={"row"} alignItems={"flex-start"} justifyContent={"space-between"} sx={{px: 3, pt: 2.5, pb: 1}}>
        <Box>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>MAIL CONNECTOR</Typography>
          <Typography sx={{fontSize: 22, fontWeight: 700, color: "#23264D"}}>
            {isEdit ? "Edit mailbox" : "Connect your mailbox"}
          </Typography>
          <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>Mails are sent through your mailbox's SMTP server</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={busy} sx={{borderRadius: "50% !important", color: "#8A8AA0"}}>
          <HugeiconsIcon icon={Cancel01Icon} size={18}/>
        </IconButton>
      </Stack>

      {loading ? (
        <Stack alignItems={"center"} gap={1} sx={{py: 6}}>
          <CircularProgress size={30} thickness={5} sx={{color: "#8491F7"}}/>
          <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>Loading mailbox…</Typography>
        </Stack>
      ) : (
        <DialogContent sx={{px: 3, display: "flex", flexDirection: "column", gap: 2.5}}>
          <Box>
            <Typography sx={sectionTitleSx}>SENDER</Typography>
            <Stack gap={1.5}>
              <TextField label={"Connector name"} size={"small"} fullWidth required value={form.name} onChange={set("name")}
                         placeholder={"e.g. Support"}/>
              <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                <TextField label={"From address"} size={"small"} fullWidth required type={"email"} autoComplete={"off"} value={form.fromAddress}
                           onChange={set("fromAddress")} placeholder={"support@company.com"}/>
                <TextField label={"From name"} size={"small"} fullWidth autoComplete={"off"} value={form.fromName} onChange={set("fromName")}
                           placeholder={"Company Support"}/>
              </Stack>
              <TextField label={"Reply-To"} size={"small"} fullWidth type={"email"} autoComplete={"off"} value={form.replyTo} onChange={set("replyTo")}
                         helperText={"Optional, where replies go"}/>
            </Stack>
          </Box>

          <Box>
            <Typography sx={sectionTitleSx}>SMTP SERVER</Typography>
            <Stack gap={1.5}>
              <TextField label={"Host"} size={"small"} fullWidth required value={form.host} onChange={set("host")}
                         placeholder={"mail.company.com"}/>
              <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                <TextField select label={"Security"} size={"small"} fullWidth value={form.security} onChange={changeSecurity}>
                  {SECURITY_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value} dense>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField label={"Port"} size={"small"} fullWidth type={"number"} value={form.port} onChange={set("port")}/>
              </Stack>
              <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                <TextField label={"Username"} size={"small"} fullWidth value={form.username} onChange={set("username")}
                           autoComplete={"off"} helperText={"Leave empty if the server needs no login"}/>
                <TextField label={"Password"} size={"small"} fullWidth type={"password"} value={form.password}
                           onChange={set("password")} autoComplete={"new-password"}
                           placeholder={passwordSet ? "••••••••" : ""}
                           helperText={passwordSet ? "Leave empty to keep the saved password" : " "}
                           InputLabelProps={passwordSet ? {shrink: true} : undefined}/>
              </Stack>
              {isEdit && passwordSet && !form.password && (
                <FormControlLabel
                  control={<Checkbox size={"small"} checked={form.removePassword} onChange={set("removePassword")}/>}
                  label={<Typography sx={{fontSize: 13, color: "#4A4A63"}}>Remove the saved password</Typography>}
                />
              )}
            </Stack>
          </Box>

          <FormControlLabel
            control={<Switch checked={form.enabled} onChange={set("enabled")}/>}
            label={<Typography sx={{fontSize: 13, color: "#4A4A63"}}>Send project mails through this mailbox</Typography>}
          />
        </DialogContent>
      )}

      <DialogActions sx={{px: 3, pb: 2.5, pt: 1, gap: 1}}>
        {isEdit && (
          <Button sx={{...buttonSx("danger"), mr: "auto"}} onClick={remove} disabled={busy || loading}
                  startIcon={deleting ? <CircularProgress size={14} sx={{color: "#C73A3A"}}/> : null}>
            Disconnect
          </Button>
        )}
        <Button sx={buttonSx("light")} onClick={onClose} disabled={busy}>Cancel</Button>
        <Button sx={buttonSx("dark")} onClick={save} disabled={busy || loading || !valid}
                startIcon={saving ? <CircularProgress size={14} sx={{color: "#fff"}}/> : null}>
          {saving ? "Saving…" : isEdit ? "Save changes" : "Connect"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
